// src/Components/videochat/CallChatOverlay.jsx
//
// Chat dentro de la llamada, dibujado sobre el video abajo a la izquierda como
// en los directos (Stripchat, TikTok Live).
//
//  - Cerrado (viendo el video): pila de los últimos 12 mensajes pegada abajo,
//    SIN scroll, que se funde arriba con el video. Los toques pasan al video.
//    Una pila alineada abajo siempre muestra lo último: con scroll, el «bajar
//    al final» a veces corría antes de que el mensaje nuevo existiera.
//  - Abierto (escribiendo): lista con scroll de los últimos 40 mensajes; cada
//    mensaje nuevo lleva al final DESPUÉS de pintarse la fila.
import React, { useLayoutEffect, useRef } from 'react'
import { FiAlertCircle } from 'react-icons/fi'
import { isGiftMessage } from './callChatUtils'

const CLOSED_COUNT = 12
const OPEN_COUNT = 40

export default function CallChatOverlay({ messages, isMine, partnerName, partnerTyping, open, reduceMotion, onRetry }) {
  const listRef = useRef(null)
  const lastId = messages.length ? messages[messages.length - 1].id : null

  // Siempre a la vista lo más nuevo: al abrir, al llegar o enviar un mensaje y al
  // cambiar el tamaño, la lista baja al final (también cerrada, que no tiene scroll
  // propio y sin esto mostraría los más viejos). Se mueve la lista, no la página.
  useLayoutEffect(() => {
    const list = listRef.current
    if (!list) return
    const toEnd = () => { list.scrollTop = list.scrollHeight }
    toEnd()
    const raf = requestAnimationFrame(toEnd)
    return () => cancelAnimationFrame(raf)
  }, [open, lastId, partnerTyping, messages.length])
  useLayoutEffect(() => {
    const list = listRef.current
    if (!list || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => { list.scrollTop = list.scrollHeight })
    ro.observe(list)
    return () => ro.disconnect()
  }, [])

  const shown = messages.slice(-(open ? OPEN_COUNT : CLOSED_COUNT))

  // Los mensajes anteriores suben deslizándose (FLIP) en vez de saltar de golpe.
  const prevTops = useRef(new Map())
  useLayoutEffect(() => {
    const list = listRef.current
    if (!list) return
    const next = new Map()
    list.querySelectorAll('[data-mid]').forEach(el => {
      const id = el.dataset.mid
      const top = el.offsetTop - list.scrollTop
      next.set(id, top)
      const before = prevTops.current.get(id)
      if (!reduceMotion && before !== undefined && before !== top) {
        el.animate([{ transform: `translateY(${before - top}px)` }, { transform: 'none' }],
          { duration: 350, easing: 'cubic-bezier(0.2,0.8,0.2,1)' })
      }
    })
    prevTops.current = next
  })

  const row = (m) => {
    const mine = isMine(m)
    const gift = isGiftMessage(m)
    const failed = !!m.failed
    return (
      <div
        key={m.id}
        data-mid={m.id}
        onClick={failed ? () => onRetry(m.id) : undefined}
        className={`vc-chat-row max-w-full shrink-0 self-start rounded-[14px] px-2.5 py-1.5 text-[16px] leading-snug text-white ${
          gift ? 'bg-[rgba(234,179,8,.45)] font-semibold shadow-[inset_0_0_0_1px_rgba(234,179,8,.8)]' : 'bg-black/[.32]'
        } ${failed ? 'cursor-pointer' : ''}`}
        style={{
          // Pastilla translúcida del ancho del texto, como la app iOS.
          textShadow: '0 1px 1.5px rgba(0,0,0,.5)',
          display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          animation: reduceMotion ? 'none' : 'vc-chat-in .35s cubic-bezier(0.2,0.8,0.2,1) both',
          opacity: m.sending ? 0.7 : 1,
          pointerEvents: open ? 'auto' : failed ? 'auto' : 'none',
        }}
      >
        {!gift && (
          <strong className={`font-semibold ${mine ? 'text-[#fda4af]' : 'text-[#eab308]'}`}>{mine ? 'Tú' : partnerName || 'Usuario'}</strong>
        )}
        {!gift && <>&nbsp;&nbsp;</>}
        {m.message}
        {failed && (
          <span className="ml-1 inline-flex items-center align-middle text-red-300" title="No se envió. Toca para reintentar">
            <FiAlertCircle size={14} aria-label="No se envió. Toca para reintentar" />
          </span>
        )}
      </div>
    )
  }

  return (
    <div
      className="absolute z-[19] flex flex-col justify-end"
      style={{
        // Alineado con la columna de 640 px de la barra de escribir; en pantallas bajas
        // se achica para no meterse debajo de la barra de arriba.
        left: 'max(12px, calc(50% - 308px))',
        width: 'min(78%, 420px)',
        height: open ? 'min(300px, calc(100% - 7.5rem - 150px))' : 'min(220px, calc(100% - 6.25rem - 170px))',
        bottom: open
          ? 'calc(4.5rem + 3rem + env(safe-area-inset-bottom, 0px))'   // encima de la barra de escribir y los regalos rápidos
          : 'calc(6.25rem + env(safe-area-inset-bottom, 0px))',        // encima de los controles
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      <style>{`.vc-chat-list{scrollbar-width:none;-ms-overflow-style:none}.vc-chat-list::-webkit-scrollbar{display:none}@keyframes vc-chat-in { from { opacity: 0; transform: translateY(24px) } to { opacity: 1; transform: none } }`}</style>
      <div
        ref={listRef}
        className={`vc-chat-list flex flex-col gap-1.5 ${open ? 'overflow-y-auto' : 'overflow-hidden'}`}
        style={{
          height: '100%',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, black 35%)',
          maskImage: 'linear-gradient(to bottom, transparent 0, black 35%)',
        }}
      >
        {/* empuja los mensajes al fondo sin romper el scroll (justify-end deja lo de arriba inalcanzable) */}
        <div className="flex-1" />
        {shown.map(row)}
        {partnerTyping && (
          <div className="shrink-0 px-1 text-[13px] text-white/90" style={{ textShadow: '0 0 3px rgba(0,0,0,.9), 0 1px 3px rgba(0,0,0,.8)' }}>
            {partnerName || 'Usuario'} está escribiendo…
          </div>
        )}
      </div>
    </div>
  )
}
